'use client';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/core/presentation/components/ui/Button';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import {
  PRODUCT_DEMO_REMINDER_SETTINGS_BANNER_BODY,
  PRODUCT_DEMO_REMINDER_SETTINGS_BANNER_TITLE,
  PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_BODY,
  PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_CTA,
  PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_SKIP,
  PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_TITLE,
  PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_BODY,
  PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_CTA,
  PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_LEAD,
  PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_SKIP,
  PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_TITLE,
  PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_BODY,
  PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_CTA,
  PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_SKIP,
  PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_TITLE,
  PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_WAIT_HINT,
} from '../constants/product-demo-reminder-settings-copy';
import {
  PRODUCT_DEMO_REMINDER_SCHEDULE_SELECTOR,
  PRODUCT_DEMO_REMINDER_WHATSAPP_TEST_SELECTOR,
  PRODUCT_DEMO_SPOTLIGHT_GAP_PX,
  PRODUCT_DEMO_SPOTLIGHT_SCROLL_SETTLE_MS,
  PRODUCT_DEMO_SPOTLIGHT_VIEWPORT_PAD_PX,
} from '../constants/product-demo-reminder-settings.constants';
import {
  computeProductDemoSpotlightStyle,
  type ProductDemoSpotlightPlacement,
} from '../utils/product-demo-spotlight-layout';
import type { ReminderSettingsDemoPhase } from '../composables/useReminderSettingsProductDemo';

export interface ReminderSettingsProductDemoLayerProps {
  demoUiActive: boolean;
  phase: ReminderSettingsDemoPhase;
  completeWelcome: () => void;
  completeWhatsappSpotlight: () => void | Promise<void>;
  completeSpotlight: () => void;
  skipDemo: () => Promise<void>;
  /** Habilita “Continuar” no spotlight do WhatsApp (confirmação do teste ou modo preview). */
  whatsappContinueEnabled: boolean;
  /**
   * Posição do cartão do spotlight em relação ao elemento destacado (teste WhatsApp).
   */
  whatsappSpotlightPlacement?: ProductDemoSpotlightPlacement;
  /**
   * Posição do cartão do spotlight em relação ao elemento destacado (periodicidade).
   */
  spotlightPlacement?: ProductDemoSpotlightPlacement;
}

type HoleRect = { top: number; left: number; width: number; height: number };

function isSpotlightPhase(p: ReminderSettingsDemoPhase): p is 'whatsapp_spotlight' | 'schedule_spotlight' {
  return p === 'whatsapp_spotlight' || p === 'schedule_spotlight';
}

/**
 * Boas-vindas + overlays com “foco” na secção de teste do WhatsApp e, depois, na periodicidade.
 * Renderizado em portal para ficar acima do shell do app.
 */
export const ReminderSettingsProductDemoLayer: React.FC<ReminderSettingsProductDemoLayerProps> = ({
  demoUiActive,
  phase,
  completeWelcome,
  completeWhatsappSpotlight,
  completeSpotlight,
  skipDemo,
  whatsappContinueEnabled,
  spotlightPlacement = 'auto',
  whatsappSpotlightPlacement = 'auto',
}) => {
  const [hole, setHole] = useState<HoleRect | null>(null);
  const [spotlightScrollReady, setSpotlightScrollReady] = useState(false);
  const [spotlightStyle, setSpotlightStyle] = useState<React.CSSProperties | null>(null);
  const skipBusyRef = useRef(false);
  const spotlightCardRef = useRef<HTMLDivElement>(null);
  const whatsappBusyRef = useRef(false);

  const spotlightSelector = useMemo(() => {
    if (phase === 'whatsapp_spotlight') return PRODUCT_DEMO_REMINDER_WHATSAPP_TEST_SELECTOR;
    if (phase === 'schedule_spotlight') return PRODUCT_DEMO_REMINDER_SCHEDULE_SELECTOR;
    return null;
  }, [phase]);

  const activeSpotlightPlacement: ProductDemoSpotlightPlacement =
    phase === 'whatsapp_spotlight' ? whatsappSpotlightPlacement : spotlightPlacement;

  /** Scroll até a secção destacada antes do overlay; só então medimos o buraco e mostramos o cartão. */
  useEffect(() => {
    if (!demoUiActive || !isSpotlightPhase(phase) || !spotlightSelector) {
      setSpotlightScrollReady(false);
      return;
    }

    setSpotlightScrollReady(false);
    let cancelled = false;
    const markReady = () => {
      if (!cancelled) setSpotlightScrollReady(true);
    };

    const el = document.querySelector(spotlightSelector);
    if (!(el instanceof HTMLElement)) {
      markReady();
      return () => {
        cancelled = true;
      };
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

    const onScrollEnd = () => markReady();
    window.addEventListener('scrollend', onScrollEnd, { passive: true });
    const fallbackTimer = window.setTimeout(markReady, PRODUCT_DEMO_SPOTLIGHT_SCROLL_SETTLE_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('scrollend', onScrollEnd);
      window.clearTimeout(fallbackTimer);
    };
  }, [demoUiActive, phase, spotlightSelector]);

  const measureHole = useCallback(() => {
    if (typeof document === 'undefined' || !spotlightSelector) return;
    const el = document.querySelector(spotlightSelector);
    if (!el || !(el instanceof HTMLElement)) {
      setHole(null);
      return;
    }
    const pad = 10;
    const r = el.getBoundingClientRect();
    setHole({
      top: Math.max(0, r.top - pad),
      left: Math.max(0, r.left - pad),
      width: r.width + pad * 2,
      height: r.height + pad * 2,
    });
  }, [spotlightSelector]);

  useLayoutEffect(() => {
    if (!demoUiActive || !isSpotlightPhase(phase) || !spotlightScrollReady) {
      setHole(null);
      return;
    }
    measureHole();
    const onResize = () => measureHole();
    window.addEventListener('resize', onResize);
    const id = window.setInterval(measureHole, 400);
    return () => {
      window.removeEventListener('resize', onResize);
      window.clearInterval(id);
    };
  }, [demoUiActive, phase, spotlightScrollReady, measureHole]);

  const updateSpotlightPosition = useCallback(() => {
    if (!hole || typeof window === 'undefined') {
      setSpotlightStyle(null);
      return;
    }
    const pad = PRODUCT_DEMO_SPOTLIGHT_VIEWPORT_PAD_PX;
    const gap = PRODUCT_DEMO_SPOTLIGHT_GAP_PX;
    const card = spotlightCardRef.current;
    const cardW = card?.offsetWidth ?? Math.min(512, window.innerWidth - pad * 2);
    const cardH = card?.offsetHeight ?? 220;

    setSpotlightStyle(
      computeProductDemoSpotlightStyle({
        hole,
        placement: activeSpotlightPlacement,
        cardWidthPx: cardW,
        cardHeightPx: cardH,
        viewportWidthPx: window.innerWidth,
        viewportHeightPx: window.innerHeight,
        viewportPadPx: pad,
        gapPx: gap,
      })
    );
  }, [hole, activeSpotlightPlacement]);

  useLayoutEffect(() => {
    if (!demoUiActive || !isSpotlightPhase(phase) || !hole) {
      setSpotlightStyle(null);
      return;
    }
    updateSpotlightPosition();
    const onResize = () => updateSpotlightPosition();
    window.addEventListener('resize', onResize);
    const card = spotlightCardRef.current;
    const ro = card ? new ResizeObserver(onResize) : null;
    if (card && ro) ro.observe(card);
    return () => {
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, [demoUiActive, phase, hole, updateSpotlightPosition]);

  useEffect(() => {
    const lockScroll =
      demoUiActive &&
      (phase === 'welcome' || (isSpotlightPhase(phase) && spotlightScrollReady));
    if (!lockScroll) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [demoUiActive, phase, spotlightScrollReady]);

  const handleSkip = useCallback(() => {
    if (skipBusyRef.current) return;
    skipBusyRef.current = true;
    void Promise.resolve(skipDemo()).finally(() => {
      skipBusyRef.current = false;
    });
  }, [skipDemo]);

  const handleWhatsappContinue = useCallback(() => {
    if (whatsappBusyRef.current || !whatsappContinueEnabled) return;
    whatsappBusyRef.current = true;
    void Promise.resolve(completeWhatsappSpotlight()).finally(() => {
      whatsappBusyRef.current = false;
    });
  }, [completeWhatsappSpotlight, whatsappContinueEnabled]);

  if (typeof document === 'undefined') return null;

  const isWhatsappSpotlightCard = phase === 'whatsapp_spotlight';
  const spotlightTitle = isWhatsappSpotlightCard ?
    PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_TITLE
  : PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_TITLE;
  const spotlightBody = isWhatsappSpotlightCard ?
    PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_BODY
  : PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_BODY;
  const spotlightCta = isWhatsappSpotlightCard ?
    PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_CTA
  : PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_CTA;
  const spotlightSkip = isWhatsappSpotlightCard ?
    PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_SKIP
  : PRODUCT_DEMO_REMINDER_SETTINGS_SPOTLIGHT_SKIP;
  const spotlightPrimary = isWhatsappSpotlightCard ? handleWhatsappContinue : completeSpotlight;
  const spotlightTitleId = isWhatsappSpotlightCard ?
    'product-demo-spotlight-whatsapp-title'
  : 'product-demo-spotlight-schedule-title';

  const banner =
    demoUiActive ?
      (
        <div
          className="pointer-events-none fixed inset-x-0 top-0 z-[99] flex justify-center px-4 pt-[calc(4.5rem+env(safe-area-inset-top,0px)+0.5rem)] sm:pt-[calc(4.5rem+env(safe-area-inset-top,0px)+0.75rem)]"
          aria-hidden={phase !== 'idle'}
        >
          <div className="pointer-events-auto max-w-2xl w-full rounded-xl border border-cyan/40 bg-navy-dark/95 px-4 py-3 shadow-lg backdrop-blur-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-cyan">
              {PRODUCT_DEMO_REMINDER_SETTINGS_BANNER_TITLE}
            </p>
            <p className="mt-1 text-sm text-white/90 leading-snug">{PRODUCT_DEMO_REMINDER_SETTINGS_BANNER_BODY}</p>
          </div>
        </div>
      )
    : null;

  const welcome =
    demoUiActive && phase === 'welcome' ?
      (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/55 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-demo-welcome-title"
        >
          <div className="w-full max-w-lg rounded-2xl border border-border-light bg-card p-4 shadow-2xl sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan/15 text-cyan">
                <AppIcon name="RocketLaunchIcon" size={24} aria-hidden />
              </div>
              <div className="min-w-0">
                <h2
                  id="product-demo-welcome-title"
                  className="text-xl font-bold text-navy-dark tracking-tight leading-tight"
                >
                  {PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_TITLE}
                </h2>
                <p className="mt-3 text-sm text-text-body leading-relaxed">{PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_LEAD}</p>
                <p className="mt-2 text-sm text-text-muted leading-relaxed">{PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_BODY}</p>
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-2 sm:mt-5 sm:flex-row sm:justify-end">
              <Button type="button" variant="primary" size="md" className="w-full sm:w-auto" onClick={completeWelcome}>
                {PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_CTA}
              </Button>
              <Button type="button" variant="outline" size="md" className="w-full sm:w-auto" onClick={handleSkip}>
                {PRODUCT_DEMO_REMINDER_SETTINGS_WELCOME_SKIP}
              </Button>
            </div>
          </div>
        </div>
      )
    : null;

  const spotlightHoleUi =
    demoUiActive && isSpotlightPhase(phase) && spotlightScrollReady && hole ?
      (
        <div className="fixed inset-0 z-[100] pointer-events-none" aria-hidden>
          <div
            className="pointer-events-auto absolute left-0 right-0 top-0 bg-black/55"
            style={{ height: hole.top }}
          />
          <div
            className="pointer-events-auto absolute left-0 right-0 bottom-0 bg-black/55"
            style={{ top: hole.top + hole.height }}
          />
          <div
            className="pointer-events-auto absolute left-0 bg-black/55"
            style={{ top: hole.top, width: hole.left, height: hole.height }}
          />
          <div
            className="pointer-events-auto absolute right-0 bg-black/55"
            style={{
              top: hole.top,
              left: hole.left + hole.width,
              width: Math.max(
                0,
                (typeof window !== 'undefined' ? window.innerWidth : 0) - hole.left - hole.width
              ),
              height: hole.height,
            }}
          />
        </div>
      )
    : null;

  const spotlightCard =
    demoUiActive && isSpotlightPhase(phase) && spotlightScrollReady && hole ?
      (
        <div
          ref={spotlightCardRef}
          className="fixed z-[102] w-[min(100vw-2rem,28rem)] max-w-lg rounded-2xl border border-border-light bg-card p-4 shadow-2xl sm:p-5"
          style={spotlightStyle ?? undefined}
          role="dialog"
          aria-modal="true"
          aria-labelledby={spotlightTitleId}
        >
          <h2
            id={spotlightTitleId}
            className="text-lg font-bold text-navy-dark tracking-tight leading-tight"
          >
            {spotlightTitle}
          </h2>
          <p className="mt-2 text-sm text-text-body leading-snug">{spotlightBody}</p>
          {isWhatsappSpotlightCard && !whatsappContinueEnabled ?
            <p className="mt-2 text-xs text-text-muted leading-snug">{PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_WAIT_HINT}</p>
          : null}
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="primary"
              size="md"
              className="w-full sm:order-2 sm:w-auto"
              disabled={isWhatsappSpotlightCard && !whatsappContinueEnabled}
              onClick={spotlightPrimary}
            >
              {spotlightCta}
            </Button>
            <Button type="button" variant="outline" size="md" className="w-full sm:order-1 sm:w-auto" onClick={handleSkip}>
              {spotlightSkip}
            </Button>
          </div>
        </div>
      )
    : null;

  const spotlightFallback =
    demoUiActive && isSpotlightPhase(phase) && spotlightScrollReady && !hole ?
      (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/55 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${spotlightTitleId}-fallback`}
        >
          <div className="w-full max-w-lg rounded-2xl border border-border-light bg-card p-4 shadow-2xl sm:p-5">
            <h2 id={`${spotlightTitleId}-fallback`} className="text-lg font-bold text-navy-dark">
              {spotlightTitle}
            </h2>
            <p className="mt-2 text-sm text-text-body leading-snug">{spotlightBody}</p>
            {isWhatsappSpotlightCard && !whatsappContinueEnabled ?
              <p className="mt-2 text-xs text-text-muted leading-snug">{PRODUCT_DEMO_REMINDER_WHATSAPP_SPOTLIGHT_WAIT_HINT}</p>
            : null}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="primary"
                size="md"
                className="w-full sm:order-2 sm:w-auto"
                disabled={isWhatsappSpotlightCard && !whatsappContinueEnabled}
                onClick={spotlightPrimary}
              >
                {spotlightCta}
              </Button>
              <Button type="button" variant="outline" size="md" className="w-full sm:order-1 sm:w-auto" onClick={handleSkip}>
                {spotlightSkip}
              </Button>
            </div>
          </div>
        </div>
      )
    : null;

  const spotlight =
    demoUiActive && isSpotlightPhase(phase) && spotlightScrollReady && hole ?
      (
        <>
          {spotlightHoleUi}
          {spotlightCard}
        </>
      )
    : spotlightFallback;

  const nodes = (
    <>
      {banner}
      {welcome}
      {spotlight}
    </>
  );

  return createPortal(nodes, document.body);
};
